import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardActionArea, 
  CardContent, 
  CardMedia,
  TextField,
  InputAdornment,
  CircularProgress,
  useTheme,
  Skeleton,
  Chip,
  Fade,
  Paper,
  Tooltip,
  IconButton,
  alpha,
  Stack,
  useMediaQuery,
  Dialog,
  Tabs,
  Tab,
  Menu,
  MenuItem,
  Button,
  Drawer,
  Divider,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Badge,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar
} from '@mui/material';
import { 
  Search as SearchIcon,
  FilterList as FilterListIcon,
  ImageOutlined as ImageIcon,
  PersonOutline as PersonIcon,
  CalendarToday as CalendarIcon,
  ViewModule as GridViewIcon,
  ViewList as ListViewIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  School as SchoolIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Clear as ClearIcon,
  LocalHospital as DiagnosisIcon,
  LocationOn as LocationIcon,
  Warning as RiskIcon,
  Palette as ColorIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  ContentCopy as ContentCopyIcon,
  Download as DownloadIcon
} from '@mui/icons-material';

import { 
  listUserCases, 
  getUniqueTaxonomies, 
  filterCasesByTaxonomies,
  deleteCase,
  migrateActiveExtractions, 
  type TaxonomyData, 
  type TaxonomyFilters 
} from '../../services/caseApi';
import { getReportsByCase } from '../../services/reportAPI';
import AuthService from '../../services/AuthService';
import TourGuide from '../../../features/components/TourGuide';
import { useSubscription } from '../../hooks/useSubscription';

interface CaseItem {
  id: string;
  caseId: string;
  fileName: string;
  filePath?: string;
  mimeType: string;
  patientId?: string;
  createdAt: string;
  userId: string;
}

// Helper function to format dates nicely
const formatDate = (dateString: string): string => {
  const options: Intl.DateTimeFormatOptions = { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  };
  return new Date(dateString).toLocaleDateString(undefined, options);
};

export default function CaseImageGallery() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // ### CHECk if this works
const initialSearch = searchParams.get('search') ?? '';
// ### CHECk if this works
const [searchTerm, setSearchTerm] = useState<string>(initialSearch);
const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>(initialSearch);

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isDarkMode = theme.palette.mode === 'dark';
  
  const [cases, setCases] = useState<CaseItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [gridSize, setGridSize] = useState<number>(3);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState<boolean>(false);
  const [zoomLevel, setZoomLevel] = useState<number>(1);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const imageRef = useRef<HTMLImageElement>(null);
  const [runTour, setRunTour] = useState<boolean>(false);
  
  // Taxonomy filter states
  const [filterDrawerOpen, setFilterDrawerOpen] = useState<boolean>(false);
  const [taxonomyData, setTaxonomyData] = useState<TaxonomyData | null>(null);
  const [loadingTaxonomies, setLoadingTaxonomies] = useState<boolean>(false);
  const [activeFilters, setActiveFilters] = useState<TaxonomyFilters>({});
  const [usingTaxonomyFilter, setUsingTaxonomyFilter] = useState<boolean>(false);
  
  // Delete confirmation states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [caseToDelete, setCaseToDelete] = useState<CaseItem | null>(null);
  
  // Copy notification state
  const [copySnackbarOpen, setCopySnackbarOpen] = useState<boolean>(false);
  const [copyMessage, setCopyMessage] = useState<string>('');
  
  // Migration state
  const [migrationLoading, setMigrationLoading] = useState<boolean>(false);
  const [migrationMessage, setMigrationMessage] = useState<string>('');
  const [migrationSnackbarOpen, setMigrationSnackbarOpen] = useState<boolean>(false);
  
  // Report counts state
  const [reportCounts, setReportCounts] = useState<Record<string, number>>({});
  
  // Usage tracking for free tier
  const { refresh: refreshUsage } = useSubscription();
  
  const ITEMS_PER_PAGE = 12;
  const BASE_URL = process.env.REACT_APP_API_URL;

  // Load taxonomies on mount
  useEffect(() => {
    const loadTaxonomies = async () => {
      try {
        setLoadingTaxonomies(true);
        const authService = new AuthService();
        const token = await authService.getAccessToken();
        
        if (token) {
          const data = await getUniqueTaxonomies(token);
          setTaxonomyData(data);
          console.log('[✓] Loaded taxonomies:', data);
        }
      } catch (error) {
        console.error('[✗] Failed to load taxonomies:', error);
      } finally {
        setLoadingTaxonomies(false);
      }
    };
    
    loadTaxonomies();
  }, []);

  // Initialize search term from URL params on mount
  useEffect(() => {
    const searchFromUrl = searchParams.get('search');
    if (searchFromUrl) {
      setSearchTerm(searchFromUrl);
      setDebouncedSearchTerm(searchFromUrl);
    }
  }, []);

  // Update URL when search term changes
  useEffect(() => {
    if (debouncedSearchTerm) {
      searchParams.set('search', debouncedSearchTerm);
      setSearchParams(searchParams);
    } else if (searchParams.has('search')) {
      searchParams.delete('search');
      setSearchParams(searchParams);
    }
  }, [debouncedSearchTerm, setSearchParams]);

  // Setup search term debouncing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch report counts for visible cases
  useEffect(() => {
    const fetchReportCounts = async () => {
      try {
        const authService = new AuthService();
        const token = await authService.getAccessToken();
        if (!token) return;

        const counts: Record<string, number> = {};
        
        // Fetch report counts for each case
        await Promise.all(
          cases.map(async (caseItem) => {
            try {
              const reports = await getReportsByCase(token, caseItem.caseId, 100, 0);
              counts[caseItem.caseId] = reports.length;
            } catch (err) {
              console.error(`Failed to fetch reports for case ${caseItem.caseId}:`, err);
              counts[caseItem.caseId] = 0;
            }
          })
        );
        
        setReportCounts(counts);
      } catch (error) {
        console.error('Failed to fetch report counts:', error);
      }
    };

    if (cases.length > 0) {
      fetchReportCounts();
    }
  }, [cases]);

  // Fetch cases whenever search term or filters change (reset list)
  useEffect(() => {
    const fetchCases = async () => {
      try {
        setLoading(true);
        const authService = new AuthService();
        const token = await authService.getAccessToken();
        
        if (!token) {
          console.error('[✗] No auth token available');
          return;
        }

        let caseData: CaseItem[];

        // Check if we have active taxonomy filters
        const hasActiveFilters = Object.keys(activeFilters).some(key => {
          const value = activeFilters[key as keyof TaxonomyFilters];
          return Array.isArray(value) ? value.length > 0 : false;
        });

        if (hasActiveFilters && usingTaxonomyFilter) {
          console.log('[→] Fetching with taxonomy filters:', activeFilters);
          caseData = await filterCasesByTaxonomies(
            token,
            activeFilters,
            debouncedSearchTerm,
            ITEMS_PER_PAGE,
            0 // Always start from offset 0 on filter/search change
          );
        } else {
          console.log('[→] Fetching with standard search');
          caseData = await listUserCases(token, debouncedSearchTerm, ITEMS_PER_PAGE, 0);
        }
        
        // Filter cases to only include images if needed
        let filteredCases = caseData;
        if (selectedTab === 1) {
          filteredCases = caseData.filter(item => item.mimeType?.startsWith('image/'));
        }
        
        setCases(filteredCases);
        setHasMore(caseData.length === ITEMS_PER_PAGE); // If we got full page, there might be more
      } catch (error) {
        console.error('[✗] Failed to fetch cases:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchCases();
  }, [debouncedSearchTerm, selectedTab, activeFilters, usingTaxonomyFilter]);
  
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };
  
  const handleViewModeChange = (mode: 'grid' | 'list') => {
    setViewMode(mode);
  };
  
  const handleGridSizeChange = (size: number) => {
    setGridSize(size);
    setAnchorEl(null);
  };
  
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };
  
  const handleImageClick = (caseItem: CaseItem) => {
    const params = new URLSearchParams();
    if (caseItem.patientId) {
      params.set("patientId", caseItem.patientId);
    }
    params.set("tab", "details");
    navigate(`/cases/${caseItem.caseId}/details?${params.toString()}`);
  };
  
  const handleImagePreview = (caseItem: CaseItem, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedImage(getImageUrl(caseItem));
    setPreviewDialogOpen(true);
    setZoomLevel(1);
    setDragPosition({ x: 0, y: 0 });
  };
  
  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.25, 3));
  };
  
  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
  };
  
  const handleResetZoom = () => {
    setZoomLevel(1);
    setDragPosition({ x: 0, y: 0 });
  };
  
  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
    }
  };
  
  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && zoomLevel > 1) {
      setDragPosition(prev => ({
        x: prev.x + e.movementX,
        y: prev.y + e.movementY
      }));
    }
  };
  
  const handleMouseUp = () => {
    setIsDragging(false);
  };
  
  const handleDialogClose = () => {
    setPreviewDialogOpen(false);
    setZoomLevel(1);
    setDragPosition({ x: 0, y: 0 });
  };
  
  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      handleZoomIn();
    } else {
      handleZoomOut();
    }
  };
  
  const handleDownloadImage = async () => {
    if (!selectedImage) return;
    
    try {
      // Fetch the image
      const response = await fetch(selectedImage);
      const blob = await response.blob();
      
      // Extract filename from URL or use default
      const urlParts = selectedImage.split('/');
      const filename = urlParts[urlParts.length - 1] || 'dermatology-image.jpg';
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download image:', error);
      alert('Failed to download image. Please try again.');
    }
  };
  
  const handleLoadMore = async () => {
    try {
      setLoadingMore(true);
      const authService = new AuthService();
      const token = await authService.getAccessToken();
      
      if (!token) return;

      const offset = cases.length; // Use current length as offset
      let newCaseData: CaseItem[];

      // Check if we have active taxonomy filters
      const hasActiveFilters = Object.keys(activeFilters).some(key => {
        const value = activeFilters[key as keyof TaxonomyFilters];
        return Array.isArray(value) ? value.length > 0 : false;
      });

      if (hasActiveFilters && usingTaxonomyFilter) {
        newCaseData = await filterCasesByTaxonomies(
          token,
          activeFilters,
          debouncedSearchTerm,
          ITEMS_PER_PAGE,
          offset
        );
      } else {
        newCaseData = await listUserCases(token, debouncedSearchTerm, ITEMS_PER_PAGE, offset);
      }
      
      // Filter cases to only include images if needed
      let filteredNewCases = newCaseData;
      if (selectedTab === 1) {
        filteredNewCases = newCaseData.filter(item => item.mimeType?.startsWith('image/'));
      }
      
      // Append new cases to existing ones
      setCases(prev => [...prev, ...filteredNewCases]);
      setHasMore(newCaseData.length === ITEMS_PER_PAGE);
    } catch (error) {
      console.error('[✗] Failed to load more cases:', error);
    } finally {
      setLoadingMore(false);
    }
  };
  
  const getImageUrl = (caseItem: CaseItem): string => {
    if (!caseItem.mimeType?.startsWith('image/')) {
      return '/assets/file-placeholder.svg';
    }
    if (caseItem.filePath) {
      return `${BASE_URL}/${caseItem.filePath.replace(/^\/+/, '')}`;
    }
    return `${BASE_URL}/testing3/${caseItem.fileName.replace(/^\/+/, '')}`;
  };
  
  // Taxonomy filter handlers
  const handleFilterDrawerToggle = () => {
    setFilterDrawerOpen(!filterDrawerOpen);
  };

  const handleMigrateExtractions = async () => {
    try {
      setMigrationLoading(true);
      const authService = new AuthService();
      const token = await authService.getAccessToken();
      
      if (token) {
        const result = await migrateActiveExtractions(token);
        
        if (result.success) {
          setMigrationMessage(
            `✓ Migration complete: ${result.updatedCount} extractions updated, ` +
            `${result.alreadySetCount || 0} already set`
          );
          // Reload taxonomies to reflect changes
          const data = await getUniqueTaxonomies(token);
          setTaxonomyData(data);
        } else {
          setMigrationMessage(`✗ Migration failed: ${result.error || 'Unknown error'}`);
        }
      }
    } catch (error: any) {
      setMigrationMessage(`✗ Migration error: ${error.message || 'Unknown error'}`);
    } finally {
      setMigrationLoading(false);
      setMigrationSnackbarOpen(true);
    }
  };

  const handleFilterChange = (filterType: keyof TaxonomyFilters, value: string[]) => {
    setActiveFilters(prev => ({
      ...prev,
      [filterType]: value.length > 0 ? value : undefined
    }));
    setUsingTaxonomyFilter(true);
  };

  const handleCharacteristicFilterChange = (charType: string, values: string[]) => {
    setActiveFilters(prev => {
      const newCharacteristics = { ...(prev.characteristics || {}) };
      if (values.length > 0) {
        newCharacteristics[charType] = values;
      } else {
        delete newCharacteristics[charType];
      }
      
      return {
        ...prev,
        characteristics: Object.keys(newCharacteristics).length > 0 ? newCharacteristics : undefined
      };
    });
    setUsingTaxonomyFilter(true);
  };

  const handleClearAllFilters = () => {
    setActiveFilters({});
    setUsingTaxonomyFilter(false);
  };

  const getActiveFilterCount = (): number => {
    let count = 0;
    if (activeFilters.primary_diagnoses?.length) count += activeFilters.primary_diagnoses.length;
    if (activeFilters.differential_diagnoses?.length) count += activeFilters.differential_diagnoses.length;
    if (activeFilters.malignancy_risks?.length) count += activeFilters.malignancy_risks.length;
    if (activeFilters.locations?.length) count += activeFilters.locations.length;
    if (activeFilters.characteristics) {
      Object.values(activeFilters.characteristics).forEach(arr => {
        count += arr.length;
      });
    }
    return count;
  };

  // Delete, View Details, and Copy handlers
  const openDeleteDialog = (caseItem: CaseItem) => {
    setCaseToDelete(caseItem);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async (caseId: string) => {
    try {
      const authService = new AuthService();
      const token = await authService.getAccessToken();
      if (!token) throw new Error('No access token available');
      
      await deleteCase(token, caseId);
      
      // Remove from local state
      setCases(prevCases => prevCases.filter(c => c.caseId !== caseId));
      
      // Refresh usage count after deletion (user now has 1 more image available)
      console.log('[🔄] Case deleted - refreshing usage count');
      if (refreshUsage) {
        refreshUsage();
      }
      
      setDeleteDialogOpen(false);
      setCaseToDelete(null);
    } catch (error) {
      console.error('Failed to delete case:', error);
      alert('Failed to delete case. Please try again.');
    }
  };

  const handleCopyId = (id: string, type: 'patient' | 'case') => {
    navigator.clipboard.writeText(id);
    setCopyMessage(`${type === 'patient' ? 'Patient' : 'Case'} ID copied to clipboard`);
    setCopySnackbarOpen(true);
  };

  const handleViewDetails = (caseItem: CaseItem) => {
    navigate(`/cases/${caseItem.caseId}/details?patientId=${caseItem.patientId || ''}`);
  };
  
  // Skeleton loader for when images are loading
  const renderSkeletons = () => {
    return Array(ITEMS_PER_PAGE).fill(0).map((_, index) => (
      <Grid item xs={12} sm={6} md={12 / gridSize} key={`skeleton-${index}`}>
        <Skeleton 
          variant="rectangular" 
          width="100%" 
          height={viewMode === 'grid' ? 200 : 100} 
          sx={{ borderRadius: 2, mb: 1 }} 
        />
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="text" width="40%" />
      </Grid>
    ));
  };
  
  // Tour steps - conditional based on whether cases exist
  const tourSteps = React.useMemo(() => {
    if (cases.length === 0) {
      // No cases exist yet - show simplified beginner tour
      return [
        {
          target: '[data-tour="gallery-search"]',
          content: '👋 Welcome to the Smart Medical Archive! This is where all your dermatology cases and images are organized. Currently, you don\'t have any cases yet.',
          disableBeacon: true,
        },
        {
          target: 'body',
          content: '📸 To get started, navigate to the "Patients" tab, create a patient, and then upload your first case with images. Once you have cases, re-run this tour to explore all the powerful features like taxonomy filtering, image preview, and advanced search!',
          disableBeacon: true,
          placement: 'center' as const,
        },
      ];
    }

    // Cases exist - show full feature tour
    const hasImages = cases.some(c => c.mimeType?.startsWith('image/'));
    
    return [
      {
        target: '[data-tour="gallery-search"]',
        content: '🔍 Search for cases by file name, patient ID, or keywords. The search updates as you type with intelligent filtering.',
        disableBeacon: true,
      },
      {
        target: '[data-tour="taxonomy-filter"]',
        content: '🏥 Advanced Medical Taxonomy Filters allow you to find cases by diagnosis, malignancy risk, anatomical location, and specific characteristics (color, size, shape, texture).',
        disableBeacon: true,
      },
      {
        target: '[data-tour="view-modes"]',
        content: '📐 Switch between grid and list views, or adjust the grid size to customize how you view your cases. Grid view is great for images, while list view shows more details.',
        disableBeacon: true,
      },
      {
        target: '[data-tour="filter-tabs"]',
        content: '🗂️ Use tabs to filter your view: "All Files" shows everything, while "Images Only" filters to just image files.',
        disableBeacon: true,
      },
      ...(hasImages ? [{
        target: '[data-tour="image-card"]',
        content: '🖼️ Each case card shows the image preview, creation date, patient ID (if assigned), and case ID. Click the card to view full details, or use the zoom icon (🔍) to preview the image. You can also delete cases or copy IDs with the action buttons.',
        disableBeacon: true,
      }] : []),
    ];
  }, [cases.length]);

  const handleStartTour = (e: React.MouseEvent) => {
    e.preventDefault();
    localStorage.removeItem("tour_image-gallery_completed");
    localStorage.removeItem("tour_image-gallery_step");
    setRunTour(false);
    setTimeout(() => setRunTour(true), 50);
  };

  const handleFinishTour = () => {
    setRunTour(false);
  };
  
  const activeFilterCount = getActiveFilterCount();

  return (
    <Box sx={{ maxWidth: '100%', mb: 4 }}>
      <Paper
        elevation={2}
        sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: 2,
          mb: 3,
          background: isDarkMode 
            ? 'linear-gradient(to bottom, rgba(35,38,45,0.9), rgba(25,28,35,1))'
            : 'linear-gradient(to bottom, rgba(255,255,255,0.9), rgba(248,248,252,1))',
          boxShadow: isDarkMode
            ? '0 8px 32px rgba(0,0,0,0.3)'
            : '0 8px 32px rgba(100,100,255,0.1)',
        }}
      >
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          spacing={2} 
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          mb={3}
        >
          <Typography 
            variant={isSmallScreen ? "h6" : "h5"} 
            component="h1" 
            fontWeight={600}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <ImageIcon fontSize={isSmallScreen ? "medium" : "large"} />
            Smart Medical Archive
          </Typography>
          
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ display: 'flex', gap: 1 }} data-tour="view-modes">
              <Tooltip title="Grid View">
                <IconButton 
                  color={viewMode === 'grid' ? 'primary' : 'default'} 
                  onClick={() => handleViewModeChange('grid')}
                  sx={{ 
                    bgcolor: viewMode === 'grid' ? alpha(theme.palette.primary.main, 0.1) : 'transparent' 
                  }}
                >
                  <GridViewIcon />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="List View">
                <IconButton 
                  color={viewMode === 'list' ? 'primary' : 'default'} 
                  onClick={() => handleViewModeChange('list')}
                  sx={{ 
                    bgcolor: viewMode === 'list' ? alpha(theme.palette.primary.main, 0.1) : 'transparent' 
                  }}
                >
                  <ListViewIcon />
                </IconButton>
              </Tooltip>
              
              {viewMode === 'grid' && (
                <>
                  <Tooltip title="Adjust Grid Size">
                    <IconButton 
                      onClick={handleMenuOpen}
                      sx={{ 
                        bgcolor: Boolean(anchorEl) ? alpha(theme.palette.primary.main, 0.1) : 'transparent' 
                      }}
                    >
                      <ZoomOutIcon />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                  >
                    <MenuItem selected={gridSize === 6} onClick={() => handleGridSizeChange(6)}>Small</MenuItem>
                    <MenuItem selected={gridSize === 4} onClick={() => handleGridSizeChange(4)}>Medium</MenuItem>
                    <MenuItem selected={gridSize === 3} onClick={() => handleGridSizeChange(3)}>Large</MenuItem>
                    <MenuItem selected={gridSize === 2} onClick={() => handleGridSizeChange(2)}>Extra Large</MenuItem>
                  </Menu>
                </>
              )}
            </Box>
            
            <Button
              onClick={handleStartTour}
              startIcon={<SchoolIcon />}
              variant="contained"
              color="secondary"
              sx={{
                backgroundColor: "#e91e63",
                color: "#ffffff",
                "&:hover": {
                  backgroundColor: "#d81b60",
                },
              }}
            >
              Guide
            </Button>
          </Stack>
        </Stack>
        
        <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }} mb={3}>
          <TextField
            fullWidth
            placeholder="Search by file name, or keywords..."
            value={searchTerm}
            onChange={handleSearchChange}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              sx: {
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.background.paper, 0.5),
              }
            }}
            data-tour="gallery-search"
          />
          
          <Tooltip title={`Advanced medical taxonomy filters${activeFilterCount > 0 ? ` (${activeFilterCount} active)` : ''}`}>
            <Badge badgeContent={activeFilterCount} color="primary">
              <Button
                variant={activeFilterCount > 0 ? "contained" : "outlined"}
                startIcon={<FilterListIcon />}
                onClick={handleFilterDrawerToggle}
                data-tour="taxonomy-filter"
                sx={{
                  minWidth: 140,
                  bgcolor: activeFilterCount > 0 ? theme.palette.primary.main : 'transparent',
                  color: activeFilterCount > 0 ? '#fff' : 'inherit',
                  '&:hover': {
                    bgcolor: activeFilterCount > 0 ? theme.palette.primary.dark : alpha(theme.palette.primary.main, 0.1),
                  }
                }}
              >
                Filters
              </Button>
            </Badge>
          </Tooltip>
          
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: '40px',
              '& .MuiTab-root': {
                minHeight: '40px',
                py: 0.5,
              },
              width: { xs: '100%', sm: 'auto' },
            }}
            data-tour="filter-tabs"
          >
            <Tab label="All Files" />
            <Tab label="Images Only" />
          </Tabs>
        </Stack>

        {/* Active Filter Chips */}
        {activeFilterCount > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" mb={2} sx={{ gap: 1 }}>
            <Chip
              label={`${activeFilterCount} filter${activeFilterCount > 1 ? 's' : ''} active`}
              onDelete={handleClearAllFilters}
              color="primary"
              size="small"
              icon={<FilterListIcon />}
            />
            {activeFilters.primary_diagnoses?.map(dx => (
              <Chip
                key={`primary-${dx}`}
                label={`Diagnosis: ${dx}`}
                size="small"
                onDelete={() => {
                  handleFilterChange('primary_diagnoses', activeFilters.primary_diagnoses?.filter(d => d !== dx) || []);
                }}
                sx={{ bgcolor: alpha(theme.palette.error.main, 0.1) }}
              />
            ))}
            {activeFilters.malignancy_risks?.map(risk => (
              <Chip
                key={`risk-${risk}`}
                label={`Risk: ${risk}`}
                size="small"
                color={risk === 'malignant' ? 'error' : risk === 'benign' ? 'success' : 'warning'}
                variant="outlined"
                onDelete={() => {
                  handleFilterChange('malignancy_risks', activeFilters.malignancy_risks?.filter(r => r !== risk) || []);
                }}
              />
            ))}
            {activeFilters.differential_diagnoses?.map(dx => (
  <Chip
    key={`diff-${dx}`}
    label={`Diff: ${dx}`}
    size="small"
    onDelete={() => {
      handleFilterChange(
        'differential_diagnoses',
        activeFilters.differential_diagnoses?.filter(d => d !== dx) || []
      );
    }}
    sx={{ bgcolor: alpha(theme.palette.warning.main, 0.08) }}
  />
))}
{activeFilters.characteristics &&
  Object.entries(activeFilters.characteristics).map(([charKey, values]) =>
    values.map(value => (
      <Chip
        key={`char-${charKey}-${value}`}
        label={`${charKey.charAt(0).toUpperCase() + charKey.slice(1)}: ${value}`}
        size="small"
        onDelete={() => {
          const current = activeFilters.characteristics?.[charKey] || [];
          const newValues = current.filter(v => v !== value);
          handleCharacteristicFilterChange(charKey, newValues);
        }}
        sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.08) }}
      />
    ))
  )}
            {activeFilters.locations?.map(loc => (
              <Chip
                key={`loc-${loc}`}
                label={`Location: ${loc}`}
                size="small"
                icon={<LocationIcon />}
                onDelete={() => {
                  handleFilterChange('locations', activeFilters.locations?.filter(l => l !== loc) || []);
                }}
                sx={{ bgcolor: alpha(theme.palette.info.main, 0.1) }}
              />
            ))}
          </Stack>
        )}

        <Grid container spacing={2}>
          {loading ? (
            renderSkeletons()
          ) : cases.length === 0 ? (
            <Box sx={{ 
              width: '100%', 
              py: 6, 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.palette.text.secondary,
              gap: 2
            }}>
              <ImageIcon sx={{ fontSize: 60, opacity: 0.5 }} />
              <Typography variant="h6">No cases found</Typography>
              <Typography variant="body2">
                {searchTerm || activeFilterCount > 0
                  ? 'Try adjusting your search criteria or filters'
                  : 'Upload some images to get started'}
              </Typography>
              {activeFilterCount > 0 && (
                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={handleClearAllFilters}
                  size="small"
                >
                  Clear All Filters
                </Button>
              )}
            </Box>
          ) : (
            cases.map((caseItem, index) => (
              <Grid item xs={12} sm={viewMode === 'list' ? 12 : 6} md={viewMode === 'list' ? 12 : 12 / gridSize} key={caseItem.id}>
                <Fade in={true} timeout={500}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      display: 'flex',
                      flexDirection: viewMode === 'list' ? 'row' : 'column',
                      borderRadius: 2,
                      overflow: 'hidden',
                      transition: 'transform 0.2s, box-shadow 0.3s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`
                      }
                    }}
                    data-tour={index === 0 ? "image-card" : undefined}
                  >
                    <CardActionArea 
                      onClick={() => handleImageClick(caseItem)} 
                      sx={{ 
                        display: 'flex', 
                        flexDirection: viewMode === 'list' ? 'row' : 'column', 
                        height: '100%', 
                        alignItems: 'stretch' 
                      }}
                    >
                      <Box sx={{ position: 'relative', width: viewMode === 'list' ? 100 : '100%' }}>
                        {reportCounts[caseItem.caseId] > 0 && (
                          <Tooltip 
                            title={
                              <Box sx={{ textAlign: 'center', py: 0.5 }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                                  {reportCounts[caseItem.caseId]} Medical Report{reportCounts[caseItem.caseId] > 1 ? 's' : ''}
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.65rem', opacity: 0.9 }}>
                                  Click to view case details
                                </Typography>
                              </Box>
                            }
                            placement="left"
                            arrow
                          >
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 10,
                                right: 10,
                                zIndex: 2,
                                animation: reportCounts[caseItem.caseId] > 0 ? 'pulse 2s ease-in-out infinite' : 'none',
                                '@keyframes pulse': {
                                  '0%, 100%': {
                                    transform: 'scale(1)',
                                  },
                                  '50%': {
                                    transform: 'scale(1.05)',
                                  },
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  position: 'relative',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  minWidth: 36,
                                  height: 36,
                                  borderRadius: '12px',
                                  background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                                  boxShadow: `
                                    0 4px 14px ${alpha(theme.palette.success.main, 0.4)},
                                    0 2px 6px ${alpha(theme.palette.success.dark, 0.3)},
                                    inset 0 1px 0 ${alpha('#fff', 0.2)}
                                  `,
                                  backdropFilter: 'blur(10px)',
                                  border: `1.5px solid ${alpha('#fff', 0.3)}`,
                                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                  cursor: 'pointer',
                                  '&:hover': {
                                    transform: 'translateY(-2px) scale(1.08)',
                                    boxShadow: `
                                      0 8px 20px ${alpha(theme.palette.success.main, 0.5)},
                                      0 4px 10px ${alpha(theme.palette.success.dark, 0.4)},
                                      inset 0 1px 0 ${alpha('#fff', 0.3)}
                                    `,
                                  },
                                  '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    borderRadius: '12px',
                                    background: `linear-gradient(135deg, ${alpha('#fff', 0.2)} 0%, transparent 100%)`,
                                    pointerEvents: 'none',
                                  },
                                }}
                              >
                                <DescriptionIcon 
                                  sx={{ 
                                    fontSize: '1rem',
                                    color: '#fff',
                                    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))',
                                    mr: reportCounts[caseItem.caseId] > 9 ? 0.3 : 0.5,
                                  }} 
                                />
                                <Typography
                                  sx={{
                                    fontSize: '0.75rem',
                                    fontWeight: 800,
                                    color: '#fff',
                                    lineHeight: 1,
                                    textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                                    letterSpacing: '-0.02em',
                                  }}
                                >
                                  {reportCounts[caseItem.caseId]}
                                </Typography>
                              </Box>
                            </Box>
                          </Tooltip>
                        )}
                        <CardMedia
                          component="img"
                          image={getImageUrl(caseItem)}
                          alt={caseItem.fileName}
                          sx={{ 
                            height: viewMode === 'list' ? 100 : 200,
                            objectFit: 'cover',
                          }}
                        />
                        {caseItem.mimeType?.startsWith('image/') && (
                          <IconButton
                            onClick={(e) => handleImagePreview(caseItem, e)}
                            sx={{
                              position: 'absolute',
                              bottom: 8,
                              right: 8,
                              bgcolor: alpha(theme.palette.background.paper, 0.7),
                              '&:hover': {
                                bgcolor: alpha(theme.palette.background.paper, 0.9)
                              }
                            }}
                            size="small"
                          >
                            <ZoomInIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                      
                      <CardContent sx={{ 
                        flexGrow: 1, 
                        p: viewMode === 'list' ? 2 : 'default',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                      }}>
                        <Box>
                          <Typography 
                            variant="subtitle1" 
                            component="div" 
                            noWrap
                            sx={{
                              fontWeight: 500,
                              mb: 0.5
                            }}
                          >
                            {caseItem.fileName.split('/').pop()?.substring(0, 30) || 'Unnamed File'}
                          </Typography>
                          
                          <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 0.5,
                              mb: 1
                            }}
                          >
                            <CalendarIcon fontSize="small" />
                            {formatDate(caseItem.createdAt)}
                          </Typography>
                        </Box>
                        
                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                          <Chip 
                            label={caseItem.mimeType?.split('/')[1] || 'unknown'} 
                            size="small"
                            variant="outlined"
                          />
                          {caseItem.patientId && (
                            <Chip 
                              icon={<PersonIcon fontSize="small" />}
                              label={`Patient: ${caseItem.patientId}`}
                              size="small" 
                              color="primary" 
                              variant="outlined"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopyId(caseItem.patientId!, 'patient');
                              }}
                              sx={{ cursor: 'pointer' }}
                            />
                          )}
                          <Chip 
                            icon={<ContentCopyIcon fontSize="small" />}
                            label={`Case: ${caseItem.caseId.substring(0, 8)}...`}
                            size="small" 
                            color="secondary" 
                            variant="outlined"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopyId(caseItem.caseId, 'case');
                            }}
                            sx={{ cursor: 'pointer' }}
                          />
                        </Stack>

                        {/* Action Buttons */}
                        <Stack 
                          direction="row" 
                          spacing={1} 
                          sx={{ mt: 'auto' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button
                            size="small"
                            startIcon={<DescriptionIcon />}
                            onClick={() => handleViewDetails(caseItem)}
                            variant="outlined"
                            sx={{ flex: 1 }}
                          >
                            Details
                          </Button>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => openDeleteDialog(caseItem)}
                            sx={{ 
                              border: `1px solid ${alpha(theme.palette.error.main, 0.5)}`,
                              borderRadius: 1
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Fade>
              </Grid>
            ))
          )}
        </Grid>
        
        {hasMore && !loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Button
              variant="outlined"
              size="large"
              onClick={handleLoadMore}
              disabled={loadingMore}
              sx={{
                minWidth: 200,
                borderRadius: 2,
                py: 1.5,
                fontWeight: 600,
                borderWidth: 2,
                '&:hover': {
                  borderWidth: 2,
                  transform: 'translateY(-2px)',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              {loadingMore ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Loading...
                </>
              ) : (
                `Load More (${cases.length} loaded)`
              )}
            </Button>
          </Box>
        )}
      </Paper>
      
      {/* Taxonomy Filter Drawer */}
      <Drawer
        anchor="right"
        open={filterDrawerOpen}
        onClose={handleFilterDrawerToggle}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 400 },
            p: 2,
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            Medical Taxonomy Filters
          </Typography>
          <IconButton onClick={handleFilterDrawerToggle} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {loadingTaxonomies ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : taxonomyData && taxonomyData.total_extractions > 0 ? (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {taxonomyData.total_extractions} analyzed case{taxonomyData.total_extractions > 1 ? 's' : ''}
              </Typography>
              {activeFilterCount > 0 && (
                <Button
                  size="small"
                  startIcon={<ClearIcon />}
                  onClick={handleClearAllFilters}
                  color="error"
                >
                  Clear All
                </Button>
              )}
            </Box>
            
            {/* Migration Button */}
            <Box sx={{ mb: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                color="info"
                size="small"
                onClick={handleMigrateExtractions}
                disabled={migrationLoading}
                startIcon={migrationLoading ? <CircularProgress size={16} /> : undefined}
              >
                {migrationLoading ? 'Migrating...' : '🔄 Migrate Legacy Data'}
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                One-time fix for taxonomy filtering issues
              </Typography>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Primary Diagnosis */}
            {taxonomyData.diagnoses.primary.length > 0 && (
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DiagnosisIcon fontSize="small" color="error" />
                    <Typography fontWeight={600}>Primary Diagnosis</Typography>
                    {activeFilters.primary_diagnoses && activeFilters.primary_diagnoses.length > 0 && (
                      <Chip size="small" label={activeFilters.primary_diagnoses.length} color="primary" />
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <FormControl fullWidth size="small">
                    <Select
                      multiple
                      value={activeFilters.primary_diagnoses || []}
                      onChange={(e) => handleFilterChange('primary_diagnoses', e.target.value as string[])}
                      input={<OutlinedInput />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {taxonomyData.diagnoses.primary.map((diagnosis) => (
                        <MenuItem key={diagnosis} value={diagnosis}>
                          <Checkbox checked={(activeFilters.primary_diagnoses || []).indexOf(diagnosis) > -1} />
                          <ListItemText primary={diagnosis} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Differential Diagnosis */}
            {taxonomyData.diagnoses.differential.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DiagnosisIcon fontSize="small" color="warning" />
                    <Typography fontWeight={600}>Differential Diagnosis</Typography>
                    {activeFilters.differential_diagnoses && activeFilters.differential_diagnoses.length > 0 && (
                      <Chip size="small" label={activeFilters.differential_diagnoses.length} color="primary" />
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <FormControl fullWidth size="small">
                    <Select
                      multiple
                      value={activeFilters.differential_diagnoses || []}
                      onChange={(e) => handleFilterChange('differential_diagnoses', e.target.value as string[])}
                      input={<OutlinedInput />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {taxonomyData.diagnoses.differential.map((diagnosis) => (
                        <MenuItem key={diagnosis} value={diagnosis}>
                          <Checkbox checked={(activeFilters.differential_diagnoses || []).indexOf(diagnosis) > -1} />
                          <ListItemText primary={diagnosis} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Malignancy Risk */}
            {taxonomyData.malignancy_risks.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RiskIcon fontSize="small" color="error" />
                    <Typography fontWeight={600}>Malignancy Risk</Typography>
                    {activeFilters.malignancy_risks && activeFilters.malignancy_risks.length > 0 && (
                      <Chip size="small" label={activeFilters.malignancy_risks.length} color="primary" />
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <FormControl fullWidth size="small">
                    <Select
                      multiple
                      value={activeFilters.malignancy_risks || []}
                      onChange={(e) => handleFilterChange('malignancy_risks', e.target.value as string[])}
                      input={<OutlinedInput />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip 
                              key={value} 
                              label={value} 
                              size="small"
                              color={
                                value === 'malignant' ? 'error' :
                                value === 'benign' ? 'success' :
                                value === 'premalignant' ? 'warning' : 'default'
                              }
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {taxonomyData.malignancy_risks.map((risk) => (
                        <MenuItem key={risk} value={risk}>
                          <Checkbox checked={(activeFilters.malignancy_risks || []).indexOf(risk) > -1} />
                          <ListItemText 
                            primary={risk}
                            primaryTypographyProps={{
                              fontWeight: risk === 'malignant' ? 600 : 400,
                              color: risk === 'malignant' ? 'error.main' : 'inherit'
                            }}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Anatomical Location */}
            {taxonomyData.locations.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationIcon fontSize="small" color="info" />
                    <Typography fontWeight={600}>Location</Typography>
                    {activeFilters.locations && activeFilters.locations.length > 0 && (
                      <Chip size="small" label={activeFilters.locations.length} color="primary" />
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <FormControl fullWidth size="small">
                    <Select
                      multiple
                      value={activeFilters.locations || []}
                      onChange={(e) => handleFilterChange('locations', e.target.value as string[])}
                      input={<OutlinedInput />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {taxonomyData.locations.map((location) => (
                        <MenuItem key={location} value={location}>
                          <Checkbox checked={(activeFilters.locations || []).indexOf(location) > -1} />
                          <ListItemText primary={location} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Characteristics */}
            {Object.values(taxonomyData.characteristics).some(arr => arr.length > 0) && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ColorIcon fontSize="small" color="secondary" />
                    <Typography fontWeight={600}>Characteristics</Typography>
                    {activeFilters.characteristics && Object.keys(activeFilters.characteristics).length > 0 && (
                      <Chip 
                        size="small" 
                        label={Object.values(activeFilters.characteristics).flat().length} 
                        color="primary" 
                      />
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={2}>
                    {taxonomyData.characteristics.colors.length > 0 && (
                      <FormControl fullWidth size="small">
                        <InputLabel>Color</InputLabel>
                        <Select
                          multiple
                          label="Color"
                          value={activeFilters.characteristics?.color || []}
                          onChange={(e) => handleCharacteristicFilterChange('color', e.target.value as string[])}
                          renderValue={(selected) => selected.join(', ')}
                        >
                          {taxonomyData.characteristics.colors.map((color) => (
                            <MenuItem key={color} value={color}>
                              <Checkbox checked={(activeFilters.characteristics?.color || []).indexOf(color) > -1} />
                              <ListItemText primary={color} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}

                    {taxonomyData.characteristics.sizes.length > 0 && (
                      <FormControl fullWidth size="small">
                        <InputLabel>Size</InputLabel>
                        <Select
                          multiple
                          label="Size"
                          value={activeFilters.characteristics?.size || []}
                          onChange={(e) => handleCharacteristicFilterChange('size', e.target.value as string[])}
                          renderValue={(selected) => selected.join(', ')}
                        >
                          {taxonomyData.characteristics.sizes.map((size) => (
                            <MenuItem key={size} value={size}>
                              <Checkbox checked={(activeFilters.characteristics?.size || []).indexOf(size) > -1} />
                              <ListItemText primary={size} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}

                    {taxonomyData.characteristics.shapes.length > 0 && (
                      <FormControl fullWidth size="small">
                        <InputLabel>Shape</InputLabel>
                        <Select
                          multiple
                          label="Shape"
                          value={activeFilters.characteristics?.shape || []}
                          onChange={(e) => handleCharacteristicFilterChange('shape', e.target.value as string[])}
                          renderValue={(selected) => selected.join(', ')}
                        >
                          {taxonomyData.characteristics.shapes.map((shape) => (
                            <MenuItem key={shape} value={shape}>
                              <Checkbox checked={(activeFilters.characteristics?.shape || []).indexOf(shape) > -1} />
                              <ListItemText primary={shape} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}

                    {taxonomyData.characteristics.textures.length > 0 && (
                      <FormControl fullWidth size="small">
                        <InputLabel>Texture</InputLabel>
                        <Select
                          multiple
                          label="Texture"
                          value={activeFilters.characteristics?.texture || []}
                          onChange={(e) => handleCharacteristicFilterChange('texture', e.target.value as string[])}
                          renderValue={(selected) => selected.join(', ')}
                        >
                          {taxonomyData.characteristics.textures.map((texture) => (
                            <MenuItem key={texture} value={texture}>
                              <Checkbox checked={(activeFilters.characteristics?.texture || []).indexOf(texture) > -1} />
                              <ListItemText primary={texture} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            )}
          </Box>
        ) : (
          <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body2">
              No taxonomy data available yet. Upload and analyze cases to enable advanced filtering.
            </Typography>
          </Box>
        )}
      </Drawer>
      
      {/* Image Preview Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
      >
        {selectedImage && (
          <Box sx={{ 
            position: 'relative',
            width: '100%',
            height: '80vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'black',
            overflow: 'hidden',
            cursor: isDragging ? 'grabbing' : (zoomLevel > 1 ? 'grab' : 'default')
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
          >
            <Box 
              ref={imageRef}
              component="img"
              src={selectedImage}
              alt="Preview"
              sx={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                transform: `scale(${zoomLevel}) translate(${dragPosition.x / zoomLevel}px, ${dragPosition.y / zoomLevel}px)`,
                transition: isDragging ? 'none' : 'transform 0.2s',
              }}
            />
            <Box sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              display: 'flex',
              gap: 1,
              bgcolor: alpha(theme.palette.background.paper, 0.2),
              borderRadius: 2,
              padding: 0.5
            }}>
              <Tooltip title="Zoom In">
                <IconButton
                  onClick={handleZoomIn}
                  sx={{
                    color: '#fff',
                    '&:hover': { bgcolor: alpha('#fff', 0.2) }
                  }}
                  size="small"
                >
                  <ZoomInIcon />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Zoom Out">
                <IconButton
                  onClick={handleZoomOut}
                  sx={{
                    color: '#fff',
                    '&:hover': { bgcolor: alpha('#fff', 0.2) }
                  }}
                  size="small"
                >
                  <ZoomOutIcon />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Reset View">
                <IconButton
                  onClick={handleResetZoom}
                  sx={{
                    color: '#fff',
                    '&:hover': { bgcolor: alpha('#fff', 0.2) }
                  }}
                  size="small"
                >
                  <Box component="span" sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>1:1</Box>
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Download Image">
                <IconButton
                  onClick={handleDownloadImage}
                  sx={{
                    color: '#fff',
                    '&:hover': { bgcolor: alpha('#fff', 0.2) }
                  }}
                  size="small"
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Close">
                <IconButton
                  onClick={handleDialogClose}
                  sx={{
                    color: '#fff',
                    '&:hover': { bgcolor: alpha('#fff', 0.2) }
                  }}
                  size="small"
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
            
            <Box sx={{
              position: 'absolute',
              bottom: 16,
              left: 16,
              color: 'white',
              bgcolor: alpha('#000', 0.5),
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              fontSize: '0.875rem'
            }}>
              {Math.round(zoomLevel * 100)}%
            </Box>
          </Box>
        )}
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete Case</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone. All data, images, and reports associated with this case will be permanently deleted.
          </Alert>
          
          {caseToDelete && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Case ID:</strong> {caseToDelete.caseId}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>File Name:</strong> {caseToDelete.fileName.split('/').pop()}
              </Typography>
              {caseToDelete.patientId && (
                <Typography variant="body2" gutterBottom>
                  <strong>Patient ID:</strong> {caseToDelete.patientId}
                </Typography>
              )}
              <Typography variant="body2" gutterBottom>
                <strong>Created:</strong> {formatDate(caseToDelete.createdAt)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={() => caseToDelete && handleDelete(caseToDelete.caseId)}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy Notification Snackbar */}
      <Snackbar 
        open={copySnackbarOpen} 
        autoHideDuration={3000}
        onClose={() => setCopySnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setCopySnackbarOpen(false)} 
          severity="success"
          sx={{ width: '100%' }}
        >
          {copyMessage}
        </Alert>
      </Snackbar>
      
      {/* Migration Notification Snackbar */}
      <Snackbar 
        open={migrationSnackbarOpen} 
        autoHideDuration={5000}
        onClose={() => setMigrationSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setMigrationSnackbarOpen(false)} 
          severity={migrationMessage.startsWith('✓') ? 'success' : 'error'}
          sx={{ width: '100%' }}
        >
          {migrationMessage}
        </Alert>
      </Snackbar>
      
      <TourGuide 
        steps={tourSteps}
        tourId="image-gallery"
        run={runTour}
        continuous={true}
        showSkipButton={true}
        showProgress={true}
        onFinish={handleFinishTour}
        onSkip={handleFinishTour}
      />
    </Box>
  );
}